/**
 * ClubPlayer — Persistent miniaturized audio player
 *
 * Docked bottom-right, above NumaAssistant.
 * Themed by current vibe (lounge/commons/arena).
 * Minimizable to a small play/pause circle.
 */

import { useState, useEffect } from 'react';
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/solid';
import { useAmbientMedia } from '../providers/AmbientMediaProvider';
import { VIBE_LABELS, VIBE_COLORS } from '@/lib/ambient-tracks';

const LS_MINIMIZED = 'tower-player-minimized';

export default function ClubPlayer() {
  const {
    isPlaying,
    currentTrack,
    volume,
    vibe,
    toggle,
    skip,
    setVolume,
  } = useAmbientMedia();

  const [minimized, setMinimized] = useState(() => {
    return localStorage.getItem(LS_MINIMIZED) === '1';
  });

  useEffect(() => {
    localStorage.setItem(LS_MINIMIZED, minimized ? '1' : '0');
  }, [minimized]);

  // Don't render if no vibe detected (not in a portal route)
  if (!vibe) return null;

  const color = VIBE_COLORS[vibe];
  const label = VIBE_LABELS[vibe];

  // Minimized — small floating circle
  if (minimized) {
    return (
      <div className="fixed bottom-16 right-4 md:bottom-20 md:right-6 z-40">
        <button
          type="button"
          onClick={toggle}
          className="flex h-10 w-10 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110"
          style={{ backgroundColor: color }}
          title={`${isPlaying ? 'Pause' : 'Play'} — ${label}`}
        >
          {isPlaying ? (
            <PauseIcon className="h-4 w-4 text-white" />
          ) : (
            <PlayIcon className="h-4 w-4 text-white" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="absolute -top-1 -left-1 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow text-[10px]"
          style={{ color }}
          title="Expand player"
        >
          <ChevronUpIcon className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-16 right-4 md:bottom-20 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-72 rounded-2xl p-3 shadow-xl"
      style={{
        backgroundColor: '#1a1a1a',
        border: `1px solid ${color}40`,
      }}
    >
      {/* Header: vibe label + minimize */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="rounded p-0.5 text-white/40 hover:text-white/70 transition-colors"
          title="Minimize"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Track info */}
      <div className="mb-2">
        <p className="text-xs font-semibold text-white truncate">
          {currentTrack?.title || 'No track loaded'}
        </p>
        {currentTrack?.artist && (
          <p className="text-[10px] text-white/40">{currentTrack.artist}</p>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        {/* Play/Pause */}
        <button
          type="button"
          onClick={toggle}
          className="flex h-8 w-8 items-center justify-center rounded-full transition-colors"
          style={{ backgroundColor: color }}
        >
          {isPlaying ? (
            <PauseIcon className="h-4 w-4 text-white" />
          ) : (
            <PlayIcon className="h-4 w-4 text-white" />
          )}
        </button>

        {/* Skip */}
        <button
          type="button"
          onClick={skip}
          className="text-white/50 hover:text-white transition-colors"
          title="Next track"
        >
          <ForwardIcon className="h-4 w-4" />
        </button>

        {/* Volume */}
        <div className="flex flex-1 items-center gap-1.5">
          <button
            type="button"
            onClick={() => setVolume(volume > 0 ? 0 : 0.3)}
            className="text-white/40 hover:text-white/70 transition-colors"
          >
            {volume === 0 ? (
              <SpeakerXMarkIcon className="h-3.5 w-3.5" />
            ) : (
              <SpeakerWaveIcon className="h-3.5 w-3.5" />
            )}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="flex-1 h-1 appearance-none rounded-full bg-white/10 accent-current"
            style={{ accentColor: color }}
          />
        </div>
      </div>
    </div>
  );
}
